import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Calendar,
  LogOut,
  Stethoscope,
  Building2,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/Logo";
import ProfileModal from "../components/ProfileModal";
import DotCoinIcon from "../components/DotCoinIcon";
import { DotCoinsProvider, useDotCoins } from "../contexts/DotCoinsContext";

const CoinChip = () => {
  const { balance } = useDotCoins();
  return (
    <NavLink
      to="/app/dotcoins"
      data-testid="header-dotcoins-indicator"
      className="mt-4 flex items-center justify-between gap-2 rounded-lg border border-[#14B8A6]/30 bg-gradient-to-r from-[#14B8A6]/10 to-[#0F2A5F]/5 px-3 py-2 transition-colors hover:border-[#14B8A6]"
    >
      <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#0F2A5F]">
        <DotCoinIcon size={22} /> DotCoins
      </span>
      <span data-testid="header-dotcoins-balance" className="text-base font-bold tabular-nums text-[#0F2A5F]">
        {balance ?? "—"}
      </span>
    </NavLink>
  );
};

const AppLayout = () => {
  const { doctor, logout, loading, refresh } = useAuth();
  const [profileOpen, setProfileOpen] = useState(false);
  const nav = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        Loading...
      </div>
    );
  }

  if (!doctor) {
    nav("/login", { replace: true });
    return null;
  }

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? "bg-[#0F2A5F] text-white"
        : "text-slate-700 hover:bg-slate-100"
    }`;

  return (
    <DotCoinsProvider>
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-white border-r border-slate-200 flex flex-col fixed inset-y-0 left-0 z-30">
        <div className="px-5 py-5 border-b border-slate-100">
          {doctor?.clinic_logo ? (
            <div className="flex items-center gap-2">
              <img src={doctor.clinic_logo} alt="Clinic logo" className="h-9 w-9 object-contain rounded" />
              <div className="min-w-0">
                <div className="text-sm font-bold text-[#0F2A5F] truncate">{doctor.clinic_name || "Your Clinic"}</div>
                <div className="text-[10px] text-slate-500 truncate">DotMedy.com</div>
              </div>
            </div>
          ) : (
            <Logo size="sm" />
          )}
          <CoinChip />
        </div>
        <nav className="flex-1 px-3 py-5 space-y-1">
          <NavLink to="/app/dashboard" className={linkCls}>
            <LayoutDashboard size={18} /> Dashboard
          </NavLink>
          <NavLink to="/app/patients" className={linkCls}>
            <Users size={18} /> Patients
          </NavLink>
          <NavLink to="/app/appointments" className={linkCls}>
            <Calendar size={18} /> Appointments
          </NavLink>
          <NavLink to="/app/dotcoins" className={linkCls} data-testid="nav-dotcoins">
            <DotCoinIcon size={18} /> DotCoins
          </NavLink>
        </nav>
        <div className="px-4 py-4 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[#0F2A5F] text-white flex items-center justify-center">
              <Stethoscope size={16} />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800 truncate">
                {doctor.name}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {doctor.clinic_name || doctor.email}
              </div>
            </div>
          </div>
          <button
            onClick={() => setProfileOpen(true)}
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-[#0F2A5F] hover:bg-slate-100 py-2 rounded-md transition-colors mb-1"
          >
            <Building2 size={16} /> Clinic Details
          </button>
          <button
            onClick={logout}
            className="w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-[#14B8A6] hover:bg-red-50 py-2 rounded-md transition-colors"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 ml-64 min-h-screen">
        <Outlet />
      </main>

      <ProfileModal
        open={profileOpen}
        doctor={doctor}
        onClose={() => setProfileOpen(false)}
        onSaved={() => refresh?.()}
      />
    </div>
    </DotCoinsProvider>
  );
};

export default AppLayout;
